<template>
	<div
		v-if="processedItems?.length"
		class="rewards-group"
		:class="`rewards-group--count-${count} rewards-group--${context} rewards-group--${isEvent ? `event ${attributes.type}` : 'normal'} ${isBordered ? 'rewards-group--bordered' : ''}`"
	>
		<BidirectionalTextLabel
			v-if="isEvent"
			:text="translate('event.event')"
			:theme="eventTextLabelTheme"
			class="rewards-label"
		/>
		<div
			v-for="(item, idx) in processedItems"
			:key="idx"
			class="rewards-group__item"
		>
			<Tooltip
				v-if="item"
				:heading="
					isEvent
						? translate(eventResourceHeadings[item.key] ?? '')
						: isMultiplier(item.key as ResourceType)
							? multiplierHeading(
									translate(`resource.${item.key}`),
									item.qty,
								)
							: translate(`resource.${item.key}`)
				"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<div
						v-if="
							Object.values(EventUpgradesEnum).includes(
								item.key as EventUpgradesEnum,
							)
						"
						class="computed-value"
						:style="{ fontSize: `${dynamicSize.price / 16}rem` }"
					>
						<span v-if="item?.attributes?.income">
							{{ `+${item.attributes.income}/${TICK_DURATION}s` }}
						</span>
						<span v-if="item?.attributes?.percentage">
							{{ `+${item.attributes.percentage}%` }}
						</span>
						<span v-if="item?.attributes?.value">
							{{ `+${item.attributes.value}` }}
						</span>
						<div
							v-if="
								Object.values(EventUpgradesEnum).includes(
									item.key as EventUpgradesEnum,
								)
							"
							class="rewards-grid__icon"
							:class="`${item?.attributes?.icon}-${dynamicSize.priceIcon}`"
						/>
					</div>
					<div
						v-else-if="isMultiplier(item.key as ResourceType)"
						class="computed-value"
					>
						<span
							:style="{
								fontSize: `${dynamicSize.price / 16}rem`,
							}"
						>
							{{
								`${formatNumber(multiplierMinValue(item.qty))} min`
							}}
						</span>
						<AppMainIcon
							:iconName="item.key.toLowerCase()"
							:iconSize="dynamicSize.priceIcon"
							:style="{
								marginTop: `${dynamicSize.price / 8 / 16}rem`,
							}"
						/>
					</div>
					<AppPrice
						v-else
						:amount="item.qty"
						:currency="item.key as ResourceType"
						:fontSize="dynamicSize.price"
						:iconSize="dynamicSize.priceIcon"
						:postfix="item?.attributes?.percentage ? '%' : null"
					/>
				</template>
				<template #tooltip>
					<span class="reward-tooltip">
						{{
							isEvent
								? replacePlaceholder(
										replacePlaceholder(
											replacePlaceholder(
												translate(
													eventResourceDescriptions[
														item.key
													] ?? "",
												),
												"{percent}",
												`${item?.attributes?.percentage ?? 0}%`,
											),
											"{number}",
											+(
												item?.attributes?.income ??
												item?.attributes?.value ??
												0
											),
										),
										"{building}",
										translate(
											`${item?.attributes?.building}.name`,
										),
									)
								: replacePlaceholder(
										translate(
											`resource.${item.key}_description`,
										),
										"{number}",
										getReplaceValue(item.key) ?? 0,
									)
						}}
					</span>
				</template>
			</Tooltip>
		</div>
		<div v-if="isBordered" class="reward-frame-container">
			<RewardFrame />
		</div>
		<RectangleBoxContainer v-if="isEvent" />
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppPrice from "@/components/AppPrice.vue";
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import RewardFrame from "@/components/SVG/RewardFrame.vue";
import Tooltip from "@/components/Tooltip.vue";
import { TICK_DURATION } from "@/config/constants";
import { useEventStore } from "@/event";
import type { ResourceType } from "@/game/entities/config/resources";
import type { UpgradeItem } from "@/game/entities/config/upgrades";
import {
	EventUpgradeIds,
	eventUpgrades,
} from "@/game/entities/config/upgrades/event";
import type { ResourceOrUtilitySlotItem } from "@/types/shop";
import {
	EventUpgrades as EventUpgradesEnum,
	type EventResourceSlotItem,
} from "@/types/shop";
import {
	EventCategory,
	LabelThemes,
	Placements,
	RewardsGridContext,
} from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { getNumber } from "@/utils/number";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import {
	getEventResourceDescriptions,
	getEventResourceHeadings,
} from "@/utils/shop/texts";
import {
	getReplaceValue,
	isMultiplier,
	multiplierHeading,
	multiplierMinValue,
} from "@/utils/shop/values";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	items: ResourceOrUtilitySlotItem[] | EventResourceSlotItem[] | null;
	context: RewardsGridContext;
	isBordered?: boolean;
	isEvent?: boolean;
}

const {
	items,
	context,
	isBordered = false,
	isEvent = false,
} = defineProps<Props>();

const count = computed((): number => items?.length ?? 0);

const eventStore = useEventStore();
const { attributes } = storeToRefs(eventStore);

const dynamicSize = computed((): Record<string, number> => {
	if (context === RewardsGridContext.POPUP) {
		switch (count.value) {
			case 1:
				return { price: 24, priceIcon: 26 };
			case 2:
				return { price: 18, priceIcon: 20 };
			case 3:
				return { price: 18, priceIcon: 20 };
			case 4:
				return { price: 18, priceIcon: 20 };
			case 5:
				return { price: 18, priceIcon: 20 };
			case 6:
				return { price: 18, priceIcon: 20 };
			default:
				return { price: 20, priceIcon: 22 };
		}
	} else {
		switch (count.value) {
			case 1:
				return { price: 14, priceIcon: 16 };
			case 2:
				return { price: 14, priceIcon: 16 };
			case 3:
				return { price: 8, priceIcon: 8 };
			case 4:
				return { price: 8, priceIcon: 8 };
			case 5:
				return { price: 8, priceIcon: 8 };
			case 6:
				return { price: 8, priceIcon: 8 };
			default:
				return { price: 20, priceIcon: 10 };
		}
	}
});

const eventTextLabelTheme = computed((): LabelThemes => {
	const type = attributes.value?.type;

	if (!type) {
		return LabelThemes.GREEN;
	}

	const typeToThemeMap = {
		[EventCategory.CHRISTMAS]: LabelThemes.RED,
		[EventCategory.EASTER]: LabelThemes.MID_GREEN,
		[EventCategory.FISHING]: LabelThemes.DARK_BLUE,
		[EventCategory.FIREFEST]: LabelThemes.RED,
		[EventCategory.SOLAR]: LabelThemes.BRONZE,
		[EventCategory.HUNTING]: LabelThemes.PINE_GREEN,
		[EventCategory.HALLOWEEN]: LabelThemes.MIDNIGHT_BLUE,
		[EventCategory.PIRATES]: LabelThemes.DARK_BLUE,
	};

	return typeToThemeMap[type] || LabelThemes.GREEN;
});

const eventResourceHeadings = computed(
	(): Record<string, string> => getEventResourceHeadings(),
);

const eventResourceDescriptions = computed(
	(): Record<string, string> => getEventResourceDescriptions(),
);

// For event phantom rewards
type ExtendedResourceSlotItem = Partial<UpgradeItem> &
	(ResourceOrUtilitySlotItem | EventResourceSlotItem);

// For event phantom rewards
const processedItems = computed((): ExtendedResourceSlotItem[] => {
	if (!items) return [];

	return items.reduce(
		(
			acc: ExtendedResourceSlotItem[],
			item: ResourceOrUtilitySlotItem | EventResourceSlotItem,
		): ExtendedResourceSlotItem[] => {
			const upgradeKey = item.key as EventUpgradesEnum;
			const isUpgrade =
				Object.values(EventUpgradesEnum).includes(upgradeKey);
			const isConversionRate = item.key.includes(
				"event_conversion_offers_rate_",
			);

			if (isUpgrade) {
				const upgradeId =
					EventUpgradeIds[upgradeKey as keyof typeof EventUpgradeIds];
				const upgradeData = eventUpgrades.get(upgradeId) ?? null;

				acc.push({
					...item,
					...upgradeData,
				});
			} else if (isConversionRate) {
				acc.push({
					...item,
					qty: getNumber(item.key),
					attributes: {
						percentage: getNumber(item.key), // For text {percent} replacement
					},
				});
			} else {
				acc.push(item);
			}

			return acc;
		},
		[],
	);
});
</script>

<style lang="scss" scoped>
.rewards-group {
	@include flex-row-center;
	position: relative;
	flex-wrap: wrap;
	gap: 1.125rem;
	z-index: 1;
	padding: 0.25rem 0.625rem 0.625rem;

	&--bordered {
		margin: 0 1rem 1rem 1rem;
		background: black;
		min-height: 4.375rem;
	}

	&--event {
		@include flex-row-center;
		position: relative;
		z-index: 3;
		margin-right: -0.125rem;
		margin-bottom: -0.125rem;
		margin-left: -0.125rem;
		box-shadow: inset 0 0 0.875rem 0 rgba(255, 179, 48, 0.7);
		padding: 1.25rem 0.625rem 0.625rem;
		width: calc(100% + 0.25rem);
		height: 100%;
		min-height: 4.5rem;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			mix-blend-mode: color-burn;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 40%;
			width: 100%;
			height: 100%;
			content: "";
		}

		&.christmas {
			background: #640101;
		}

		&.easter {
			background: #334002;
		}

		&.fishing {
			background: #034258;
		}

		&.firefest {
			background: #80001e;
		}

		&.solarfair {
			background: #6c3c04;
		}

		&.hunting {
			background: #094209;
		}

		&.halloween {
			background: #221456;
		}

		&.pirates {
			background: #034258;
		}

		.rewards-label {
			position: absolute;
			top: -1rem;
			z-index: 1;
			width: auto;
			height: 2rem;
		}
	}

	&--card {
		.rewards-group__value {
			&::before {
				display: none; // for radial-glow
			}
		}
	}

	.price {
		:deep(.shrink-text) {
			color: #fff3dd;
		}
	}

	&__item {
		position: relative;
		z-index: 1;

		.v-popper {
			width: 100%;
		}

		:deep(.computed-value) {
			@include flex-row-center;
			gap: 0.25rem;
			z-index: 1;
			margin: 0 0.2rem;
			color: #fff3dd;
			font-weight: 800;

			span {
				padding-bottom: 0.25rem;
			}
		}
	}

	&__value {
		position: relative;
		width: 100%;
		height: 7rem;

		.rewards-group__icon {
			position: absolute; // to prevent the sprite from overflowing the grid with transparent areas
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
		}
	}

	&__label {
		@include flex-row-center;
		position: relative;
		mask-image: linear-gradient(
			to right,
			transparent 2%,
			black 20%,
			black 80%,
			transparent 98%
		);
		border-width: 0.063rem 0 0.063rem 0;
		border-style: solid;
		border-image-source: linear-gradient(
			to bottom,
			rgba(216, 169, 114, 1),
			rgba(226, 181, 145, 1)
		);
		border-image-slice: 1;
		width: 100%;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			@include background(
				url($path-textures + "silk-flag-texture-bg.avif"),
				cover,
				center center,
				no-repeat
			);
			z-index: 1;
			mix-blend-mode: multiply;
			width: 100%;
			height: 100%;
			content: "";
		}

		&::after {
			position: absolute;
			top: 0;
			left: 0;
			background: rgb(112, 62, 16);
			width: 100%;
			height: 100%;
			content: "";
		}

		.price {
			z-index: 1;
		}
	}

	.reward-frame-container {
		position: absolute;
		top: 0;
		left: 0;
		z-index: -1;
		width: 100%;
		height: 100%;
	}
}
</style>
